import type { IIdentityInfo, ITicketInfo, Ticket } from "@kiraah-shared/common";
import { LvfTicketArticleNature } from "@kiraah-shared/common";

/** Base64 / token chiffré renvoyé à la place d'un nom lisible (PII). */
export function mobileIsLikelyEncryptedPersonLabel(value: string | null | undefined): boolean {
  const v = value?.trim();
  if (!v || v.length < 20) {
    return false;
  }
  if (/\s/.test(v)) {
    return false;
  }
  return /^[A-Za-z0-9+/=_-]+$/.test(v);
}

export function mobileSanitizePersonLabel(value: string | null | undefined): string {
  const v = value?.trim();
  if (!v || mobileIsLikelyEncryptedPersonLabel(v)) {
    return "";
  }
  return v;
}

export function mobileIdentityPersonLabel(identity: IIdentityInfo | null | undefined): string {
  if (!identity) {
    return "";
  }
  const fromParts = mobileSanitizePersonLabel(
    [identity.FirstName, identity.BirthName ?? identity.MarriedName].filter(Boolean).join(" "),
  );
  if (fromParts) {
    return fromParts;
  }
  return (
    mobileSanitizePersonLabel(identity.FullName) ||
    mobileSanitizePersonLabel(identity.Email) ||
    ""
  );
}

export function mobileTicketIsProductLike(t: Pick<ITicketInfo, "TicketType">): boolean {
  const n = t.TicketType?.LvfTicketArticleNature;
  return (
    n === LvfTicketArticleNature.PERISHABLE_PRODUCT || n === LvfTicketArticleNature.SERVICE
  );
}

export function mobileTicketIsVehicleLike(t: Pick<ITicketInfo, "TicketType">): boolean {
  const n = t.TicketType?.LvfTicketArticleNature;
  return n === LvfTicketArticleNature.VEHICLE || n === LvfTicketArticleNature.VEHICLE_TYPE;
}

/**
 * Pastille « type de demande » — mêmes couleurs que la grille web
 * (`.ticket-type-badge.badge-vehicle` / `.badge-product` dans `ticket-list.component.scss`).
 */
export function mobileTicketTypeBadgeClass(t: Pick<ITicketInfo, "TicketType">): string {
  if (mobileTicketIsVehicleLike(t)) {
    return "bg-[rgba(156,39,176,0.15)] text-[#9C27B0]";
  }
  return "bg-[rgba(33,150,243,0.15)] text-[#2196F3]";
}

/** Matériel : véhicule vs inventaire — comme la grille web. */
export function mobileTicketTypeMaterialIcon(t: Pick<ITicketInfo, "TicketType">): string {
  return mobileTicketIsVehicleLike(t) ? "directions_car" : "inventory_2";
}

/**
 * Libellé « type de demande » : aligné sur {@link TicketListComponent.getTicketTypeLabel}
 * (basé sur `LvfTicketArticleNature`, pas sur `TicketType.Label` métier comme « Article » ou « Achat 3 »).
 */
export function mobileTicketTypeDisplayLabel(t: Pick<ITicketInfo, "TicketType">): string {
  const nature = t.TicketType?.LvfTicketArticleNature;
  if (nature === LvfTicketArticleNature.VEHICLE || nature === LvfTicketArticleNature.VEHICLE_TYPE) {
    return nature === LvfTicketArticleNature.VEHICLE ? "Véhicule" : "Type véhicule";
  }
  if (nature === LvfTicketArticleNature.SERVICE) {
    return "Service";
  }
  return "Produit";
}

export function mobileTicketRequesterLabel(t: ITicketInfo | Ticket): string {
  if (t.RequesterTeamUid && t.RequesterTeam?.Name?.trim()) {
    return mobileSanitizePersonLabel(t.RequesterTeam.Name);
  }
  if (t.RequesterIdentityUid && t.RequesterIdentity) {
    const label = mobileIdentityPersonLabel(t.RequesterIdentity);
    if (label) {
      return label;
    }
  }
  if (t.SubDivisionUid && t.SubDivision?.Name?.trim()) {
    return mobileSanitizePersonLabel(t.SubDivision.Name);
  }
  return (
    mobileSanitizePersonLabel(t.RequesterTeam?.Name) ||
    mobileIdentityPersonLabel(t.RequesterIdentity) ||
    mobileSanitizePersonLabel(t.SubDivision?.Name) ||
    ""
  );
}

export function mobileTicketRecipientLabel(t: ITicketInfo | Ticket): string {
  const d = t.Delivery;
  if (d?.RecipientTeamUid && d.RecipientTeam?.Name?.trim()) {
    return mobileSanitizePersonLabel(d.RecipientTeam.Name);
  }
  if (d?.RecipientIdentityUid && d.RecipientIdentity) {
    const label = mobileIdentityPersonLabel(d.RecipientIdentity);
    if (label) {
      return label;
    }
  }
  return (
    mobileIdentityPersonLabel(d?.RecipientIdentity) ||
    mobileSanitizePersonLabel(d?.RecipientTeam?.Name) ||
    ""
  );
}
